import React, { useEffect, useState, useRef } from 'react';
import { Box, FormControl, Grid, IconButton, MenuItem, Select, Stack, Typography, TextareaAutosize } from '@mui/material';
import { getResourceGroups, getResourceGroupSectors, patchResourceGroup, getResourceListMapping, getResourceSearchFilterColumn } from '../../../service/service';

import type { Column } from 'react-data-grid';
import { CustomizedDataGrid } from '../../customized_data_grid';
import '../resourceGroups/resourceGroup.scss';
import Loader from '../../Loader/loader';
import { useSearchParams } from 'react-router-dom';
import { ReactComponent as EditIcon } from '../../../assets/material-symbols_edit-rounded.svg';
import Swal from 'sweetalert2';
import { textEditor } from 'react-data-grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import { BulkUpdate } from './bulkUpdate';
import { ReactComponent as SearchIcon } from '../../../assets/search-icon.svg';
import { useTheme } from "@mui/material/styles";
import HighlightedText from 'components/GlobalSearch/HighlightText';



interface ResourceGroupListingProps {
	sharedValue: any;
	updateSharedValue: (value: any) => void;
}

	const ResourceGroupListing: React.FC<ResourceGroupListingProps> = ({ sharedValue, updateSharedValue } : any) => {
	const swalOptions = {
		showCancelButton: true,
		confirmButtonText: 'Yes, update it!',
		cancelButtonText: 'No, cancel',
		customClass: {
			container: 'custom-swal-container',
			title: 'custom-swal-title',
			htmlContainer: 'custom-swal-html-container',
			confirmButton: 'custom-swal-confirm-button',
			cancelButton: 'custom-swal-cancel-button'
		}
	};
	interface Row {
		id: string;
		Resource: string;
		ResourceGroup: string;
		DFProjectcode: string;
		DFSectorname: string;
		Environment: string;
		ProjectName: string;
		BillingInformation: string;
		Subscription: string;
		MeterCategory: string;
		MeterName: string;
        selected: boolean;
		DAI: string;
	}
	
	// ---------------- Formatter Functions ----------------
	const selectedFormatter = (row: any) => {
	const checked = row.selected;
	return (
		<input
		type="checkbox"
		className="transparent-checkbox row-checkbox"
		checked={checked}
		onChange={(e) => {
			const isChecked = e.target.checked;
			const updatedRow = { ...row, selected: isChecked };
			const headerCheckboxInput = document.getElementById('HeaderCheckbox') as HTMLInputElement;

			if (isChecked) {
			selectedRows.push(row);
			selectedRowsId.push(row.id);
			} else {
			selectedRowsId.splice(selectedRowsId.indexOf(row.id), 1);

			if (headerCheckboxInput) headerCheckboxInput.checked = false;
			}

			selectedRows = selectedRows.filter((eachRow: any) => selectedRowsId.includes(eachRow.id));

			if (selectedRows.length === resourceGroupList.length && headerCheckboxInput) {
			headerCheckboxInput.checked = true;
			}

			setBulkSelectedRows(selectedRows);

			setResourceGroupList((prevRows) =>
			prevRows.map((prevRow) => (prevRow.id === updatedRow.id ? updatedRow : prevRow))
			);
		}}
		/>
	);
	};

	const sectorFormatter = (props: any) => (
	<>
		{props.row.DFSectorname?.length ? (
		<span>{props.row.DFSectorname}</span>
		) : (
		<span className="editable_ph">Select Sector</span>
		)}
		<EditIcon className="edit-icon" title="Double click to edit sector" />
	</>
	);

	const environmentFormatter = (props: any) => (
	<>
		{props.row.Environment?.length ? (
		<span>{props.row.Environment}</span>
		) : (
		<span className="editable_ph">Select Environment</span>
		)}
		<EditIcon className="edit-icon" title="Double click to edit Environment" />
	</>
	);

	const projectNameFormatter = (props: any) => (
	<>
		{props.row.ProjectName?.length ? (
		<span>{props.row.ProjectName}</span>
		) : (
		<span className="editable_ph">Enter Project</span>
		)}
		<EditIcon className="edit-icon" title="Double click to edit project" />
	</>
	);

	const projectCodeFormatter = (props: any) => (
	<>
		{props.row.DFProjectcode?.length ? (
		<span>{props.row.DFProjectcode}</span>
		) : (
		<span className="editable_ph">Enter Project Number</span>
		)}
		<EditIcon className="edit-icon" title="Double click to edit Project Number" />
	</>
	);

	const billingInfoFormatter = (props: any) => (
	<>
		{props.row.BillingInformation?.length ? (
		<span>{props.row.BillingInformation}</span>
		) : (
		<span className="editable_ph">Enter Billing Information</span>
		)}
		<EditIcon className="edit-icon" title="Double click to edit Billing Information" />
	</>
	);

	// ---------------- Editor Functions ----------------
	const sectorEditor = (p: any) => (
	<div>
		<FormControl variant="standard" sx={{ m: 1, minWidth: 120 }} className="row-select-drpdwn">
		<Select
			labelId="Select Sector"
			id="sector_dd"
			defaultOpen
			value={p.row.DFSectorname}
			onChange={(e: any) => {
			p.onRowChange({ ...p.row, DFSectorname: e.target.value }, true);
			}}
			label="Select Sector"
		>
			{sectorOptionsList.map((sector) => (
			<MenuItem key={sector} value={sector}>
				{sector}
			</MenuItem>
			))}
		</Select>
		</FormControl>
	</div>
	);

	const environmentEditor = (p: any) => (
	<div>
		<FormControl variant="standard" sx={{ m: 1, minWidth: 120 }} className="row-select-drpdwn">
		<Select
			labelId="Select environment"
			id="environment_dd"
			defaultOpen
			value={p.row.Environment}
			onChange={(e: any) => {
			p.onRowChange({ ...p.row, Environment: e.target.value }, true);
			}}
			label="Select environment"
		>
			{['Prod', 'Non-prod'].map((env) => (
			<MenuItem key={env} value={env}>
				{env}
			</MenuItem>
			))}
		</Select>
		</FormControl>
	</div>
	);

	function getColumns(): Array<Column<Row>> {
	return [
		{
		key: 'selected',
		name: 'HeaderCheckbox',
		formatter: ({ row }) => selectedFormatter(row),
		minWidth: 40
		},
		{ key: 'Resource', name: 'Resource', minWidth: 160 },
		{ key: 'ResourceGroup', name: 'Resource Group', minWidth: 150 },
		{
		key: 'DFSectorname',
		name: 'Sector',
		minWidth: 140,
		maxWidth: 140,
		width: 140,
		cellClass: 'has-editor',
		formatter: sectorFormatter,
		editor: sectorEditor
		},
		{
		key: 'Environment',
		name: 'Environment',
		minWidth: 160,
		maxWidth: 160,
		width: 160,
		cellClass: 'has-editor',
		formatter: environmentFormatter,
		editor: environmentEditor
		},
		{
		key: 'ProjectName',
		name: 'Project',
		minWidth: 140,
		width: 140,
		formatter: projectNameFormatter,
		cellClass: 'has-editor',
		editor: textEditor
		},
		{
		key: 'DFProjectcode',
		name: 'Project Number',
		minWidth: 180,
		width: 140,
		formatter: projectCodeFormatter,
		cellClass: 'has-editor',
		editor: textEditor
		},
		{
		key: 'BillingInformation',
		name: 'Billing Information',
		minWidth: 200,
		width: 140,
		formatter: billingInfoFormatter,
		cellClass: 'has-editor',
		editor: textEditor
		},
		{ key: 'Subscription', name: 'Subscription', minWidth: 200, width: 140 },
		{ key: 'MeterCategory', name: 'Meter Category', minWidth: 200, width: 140 },
		{ key: 'MeterName', name: 'Meter Name', minWidth: 250, width: 140 },
		{ key: 'DAIResource', name: 'D&AI', minWidth: 250, width: 140 }
	];
	}
	const defaultColumnNames = [
		'HeaderCheckbox',
		'Resource',
		'Resource Group',
		'Sector',
		'Environment',
		'Project',
		'Project Number',
		'Billing Information',
		'Subscription',
		'Meter Category',
		'Meter Name',
		'D & AI'
	];

	const theme = useTheme();
	const colors = theme.palette;

	let selectedRows: any = [];
	let selectedRowsId: any = [];
	const [bulkSelectedRows, setBulkSelectedRows] = useState<Row[]>([]);


	const dataFetchedRef = useRef(false);
	const [resourceGroupList, setResourceGroupList] = useState<Row[]>([]);
	const [loading, setLoading] = useState(false);
	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
	const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(100);
	const [searchParams, setSearchParams] = useSearchParams();
	const hasDrillDown = searchParams.has('isDrillDowned');

	const [searchFilterDetails, setFilterSearchInfo] = useState([]);

	const [filterData, setFilterData] = useState<Row[]>([]);
	const [filterInfo, setFilterInfo] = useState({} as any);
	const [sectorOptionsList, setSectorOptionsList] = useState<any[]>([]);

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		listResourceGroups(value, pageSize, sortingInfo, parseFilter(filterInfo), searchQuery);
	};

	const handlepageSizeChange = (event: any) => {
		listResourceGroups('1', event.target.value, sortingInfo, parseFilter(filterInfo), searchQuery);
	};

	const [searchQuery, setSearchQuery] = useState<{ column: string; value: string }>({ column: '', value: '' });
	const [isSearchApplied, setIsSearchApplied] = useState(false);
	const [showPopup, setShowPopup] = useState(false);

	const handlePopupOpen = () => {
		setShowPopup(true);
	};

	const handlePopupClear = () => {
		setSearchQuery({ ...searchQuery, column: '' });
		setIsSearchApplied(false);
		setShowPopup(false);
	};

	const handlePopupClose = () => {
		setShowPopup(false);
	};

	const handleApplyClick = async () => {
			setLoading(true);
			setIsSearchApplied(true);
			const element = document.getElementById('column');
			if (element instanceof HTMLTextAreaElement) {
				const value = element.value;
				setSearchQuery({ column: value, value: value });
				console.log(searchQuery, value)
				const response = await getResourceListMapping(
					'1',
					'100',
					sortingInfo,
					parseFilter(filterInfo),
					searchQuery.toString(),
					value.toString()
				);
					console.log(response)
				if (response.success) {
					handlePopupClose();
					
					const listData = response.data.result.map((row: any) => ({ ...row, selected: false }));
					setResourceGroupList(listData);
					setPaginationData(response.data.page);
					setPage(response.data.page.currentPage);
					setPageSize(response.data.page.size);
					setSortingInfo(response.data.sortingInfo ?? sortingInfo);
					setFilterData(response.data.filterOptions);
					setFilterInfo(response.data.filterInfo ?? filterInfo);
					setLoading(false);
					const resourceGroupsState = {
						page: response.data.page,
						filterOptions: response.data.filterOptions,
						pageSize: page,
						sortingInfo,
						filterInfo: filterInfo
					};
					localStorage.setItem('resourceGroupsState ', JSON.stringify(resourceGroupsState));
				} else {
					console.error('Error:', response);
					Swal.fire({
						icon: 'error',
						html: response.response.data.data.message.message
					});
					setLoading(false);
				}
			} else {
				console.error('Element with ID "column" not found or not a text area');
			}
	};

	const handleClearFilters = () => {
		setSearchQuery({ ...searchQuery, column: '' });
		setIsSearchApplied(false);
		listResourceGroups(1, 100, sortingInfo, parseFilter(filterInfo), '');
	};

	const resetSearchParams = () => {
		if (hasDrillDown) {
			searchParams.delete('bysector');
			searchParams.delete('sector');
			searchParams.delete('taggedproject');
			searchParams.delete('taggedsector');
			searchParams.delete('isDrillDowned');
			setSearchParams(searchParams);
		}
	};

	const handleHeaderSelectAll = (event: any) => {
		selectedRowsId = [];
		selectedRows = [];

		resourceGroupList.forEach((row: any) => {
			row.selected = event.target.checked;
			if (event.target.checked) {
				selectedRowsId.push(row.id);
			}
		});

		if (event.target.checked) {
			selectedRows = [...resourceGroupList];
		}

		setResourceGroupList([...resourceGroupList]);

		const inputElements: any = document.getElementsByClassName('row-checkbox');
		for (const el of inputElements) {
			if (el instanceof HTMLInputElement) {
				el.checked = event.target.checked;
			}
		}

		setBulkSelectedRows([...selectedRows]);
	};

	const handleFilter = (filterInfo: any) => {
		setFilterInfo(filterInfo);
		resetSearchParams();
		listResourceGroups(1, pageSize, sortingInfo, parseFilter(filterInfo), searchQuery);
	};

	const parseFilter = (filterInfo: any, name?: string) => {
		// Call only filterable columns and reset the filtering in case of wrong/empty column
		const parsedFilter: any = {};
		Object.keys(filterInfo).forEach((key: any) => {
			if (key !== name && filterInfo[key] && filterInfo[key].length) {
				parsedFilter[key] = filterInfo[key];
			}
		});
		return parsedFilter;
	};	

	async function searchListResourceGroups(searchKeyword: any, columnKey: any) {		
		const drillDownSector = hasDrillDown ? searchParams.get('sector') : '';
		console.log(drillDownSector)
		const drillDownBySector = hasDrillDown ? searchParams.get('bysector') : '';
		const drillDownTaggedToProject = hasDrillDown ? searchParams.get('taggedtoproject') : '';
		const drillDownTaggedToSector = hasDrillDown ? searchParams.get('taggedtosector') : '';

		const res = await getResourceSearchFilterColumn({
			filterInfo: parseFilter(filterInfo),
			drillDownTaggedToProject,
			drillDownSector,
			drillDownBySector,
			drillDownTaggedToSector,
			resource_group_id: null,
			searchQuery: searchKeyword,
			searchField: columnKey
		});
		if (res.success) {
			setFilterSearchInfo(res.data);
		}
	}

	const handleFilterSearch = (searchKeyword: any, columnKey: any) => {
		console.log(searchKeyword, columnKey)
		console.log(searchQuery)
		if(searchKeyword?.length >= 3)
		searchListResourceGroups(searchKeyword, columnKey);
	};

	const handleEditCell = (rows: any[], editData: any) => {
		const key = editData.column.key;
		const patchData: any = rows[editData.indexes[0]];
		patchData[key] = rows[editData.indexes[0]][key];
		const editedColName = editData.column.name;
		let editedValue = rows[editData.indexes[0]][key];
		Swal.fire({
			title: `${editedColName} - <i>${editedValue}</i>`,
			text: `Do you want to update ${editedColName} of resource group ${patchData.ResourceGroup}?`,
			...swalOptions
		}).then((result: any) => {
			if (result.isConfirmed) {
				updateResourceGroup(patchData, rows, key);
			}
		});
	};
	async function updateResourceGroup(patchData: any, rows: Row[], key: any) {
		setLoading(true);
		const res = await patchResourceGroup(patchData);
		if (res.success) {
			setLoading(false);
			setResourceGroupList(rows);
			if (key === 'Environment') updateSharedValue();
			listResourceGroups(page, pageSize, sortingInfo, parseFilter(filterInfo), searchQuery);
		}
		setLoading(false);
	}

	async function fetchSectorOptionsData() {
		const sectorRes = await getResourceGroupSectors('1', '100', '');
		if (sectorRes.success) {
			setSectorOptionsList(sectorRes.data.sectors);
		}
	}
		
	const handleAndGetFilterOptions = async (column: any) => {
		let filterOptions: any = {}
		const res = await getResourceSearchFilterColumn({
			filterInfo: parseFilter(filterInfo),
			drillDownTaggedToProject: '',
			drillDownSector: '',
			drillDownBySector: '',
			drillDownTaggedToSector: '',
			resource_group_id: null,
			searchQuery: '',
			searchField: column
		});
		if (res.success) {
			filterOptions[column] = res.data?.name || [];
			setFilterData(filterOptions)
		}
	}

		
	useEffect(() => {
		if (dataFetchedRef.current) return;
		dataFetchedRef.current = true;
		fetchSectorOptionsData();
		let storedResourceGroupsState = localStorage.getItem('resourceGroupsState ')
			? JSON.parse(localStorage.getItem('resourceGroupsState ') ?? '')
			: null;
		if (storedResourceGroupsState && storedResourceGroupsState.pageNumber && storedResourceGroupsState.pageSize) {
			listResourceGroups(
				1,
				storedResourceGroupsState.pageSize,
				storedResourceGroupsState.sortingInfo,
				parseFilter(storedResourceGroupsState.filterInfo),
				storedResourceGroupsState.searchQuery
			);
		} else {
			listResourceGroups(page, pageSize, sortingInfo, parseFilter(filterInfo), searchQuery);
		}

		const activeId = document.getElementById('root');
		if (activeId) {
			const mainContent = document.getElementsByClassName('main-content')[0];

			if (mainContent) {
				// Use type assertion to let TypeScript know that myElement has a style property
				(mainContent as HTMLElement).style.overflow = 'scroll';
				(mainContent as HTMLElement).style.height = 'calc(100vh - 35px)';
			}

			activeId.classList.add('jobs-list-table');
			activeId.classList.remove('root');
			document.body.classList.add('jobs-exe-datas');
			document.body.classList.add('jobs-list-datas');
		}
	}, [sharedValue]);




	async function listResourceGroups(pageNumber: any, size: any, sortingInfo: any, resourceGroupFilterInfo: any, searchQuery: any) {
		setLoading(true);
		const drillDownSector = hasDrillDown ? searchParams.get('sector') : '';
		console.log(drillDownSector)
		const drillDownBySector = hasDrillDown ? searchParams.get('bysector') : '';
		const drillDownTaggedToProject = hasDrillDown ? searchParams.get('taggedtoproject') : '';
		const drillDownTaggedToSector = hasDrillDown ? searchParams.get('taggedtosector') : '';
		
		Object.keys(resourceGroupFilterInfo).forEach(key => {
			resourceGroupFilterInfo[key] = resourceGroupFilterInfo[key].filter((value: any) => value !== null)
		});
		
		const res = await getResourceGroups({
			page: pageNumber.toString(),
			size: size.toString(),
			sortingInfo,
			filterInfo: resourceGroupFilterInfo,
			drillDownTaggedToProject,
			drillDownSector,
			drillDownBySector,
			drillDownTaggedToSector,
			resource_group_id: null,
			whereFilter: searchQuery
		});
		if (res.success) {
			const listData = res.data.result.map((row: any) => ({ ...row, selected: false }));
			setResourceGroupList(listData);
			setPaginationData(res.data.page);
			setPage(pageNumber);
			setPageSize(size);
			setSortingInfo(sortingInfo);
			setFilterData(res.data.filterOptions);
			setFilterInfo(resourceGroupFilterInfo);
			setLoading(false);
			const resourceGroupsState = {
				page: res.data.page,
				filterOptions: res.data.filterOptions,
				pageSize: size,
				pageNumber,
				sortingInfo,
				filterInfo: resourceGroupFilterInfo
			};
			localStorage.setItem('resourceGroupsState ', JSON.stringify(resourceGroupsState));
		}
	}
	const handleSorting = (sortingInfo: any) => {
		listResourceGroups(page, pageSize, sortingInfo, parseFilter(filterInfo), searchQuery);
	};
	const [editOpen, setEditOpen] = React.useState(false);

	const handleEditOpen = () => {
		console.log(selectedRows)
		setEditOpen(true);

	};
	const handleEditClose = () => {
		setEditOpen(false);
		listResourceGroups(page, pageSize, sortingInfo, parseFilter(filterInfo), searchQuery);

	};

	const handleCancel = () => {
        const updatedRows = resourceGroupList.map(row => ({ ...row, selected: false }));
        setResourceGroupList(updatedRows);
    };

	return (
		<Box className="resourceGroups_tabledata page-table">

			{/* ===== Top Bar ===== */}
			<Box
				display="flex"
				justifyContent="flex-end"
				alignItems="center"
				mb={2}
			>
				<Stack direction="row" alignItems="center" spacing={2}>
					{/* Search */}
					<IconButton onClick={handlePopupOpen}>
						<SearchIcon style={{background: `${colors.search.background}`}} />
						{isSearchApplied && (
							<Box
								component="span"
								sx={{
									position: 'absolute',
									top: 6,
									right: 6,
									width: 8,
									height: 8,
									bgcolor: `${colors.search.background}`
								}}
							/>
						)}
					</IconButton>

					{isSearchApplied && (
						<Typography
							variant="body2"
							sx={{ cursor: 'pointer', color: 'rgba(0, 208, 141, 1)' }}
							onClick={handleClearFilters}
						>
							Clear Search Filter
						</Typography>
					)}

					{/* Bulk Actions */}
					{resourceGroupList.some((row) => row.selected) && (
						<Stack direction="row" spacing={1}>
							<Button variant="outlined" onClick={handleCancel} sx={{ backgroundColor: 'rgba(0, 208, 141, 1) !important' }}>
								CANCEL
							</Button>
							<Button variant="contained" onClick={handleEditOpen} sx={{ backgroundColor: 'rgba(0, 208, 141, 1) !important' }}>
								BULK UPDATE
							</Button>
						</Stack>
					)}
				</Stack>
			</Box>

			<Grid>
				{/* ===== Search Popup ===== */}
				{showPopup && (
					<Box
						position="absolute"
						zIndex={1300}
						bgcolor='rgba(27, 30, 53, 1)'
						p={2}
						width={400}
					>
						<Box
							display="flex"
							justifyContent="space-between"
							alignItems="center"
							color="#fff"
							p={1}
						>
							<Typography variant="subHeader">
								Search From Resources
							</Typography>
							<Button onClick={handlePopupClose} color="inherit">
								X
							</Button>
						</Box>

						<Box component="fieldset" mt={2}>
							<Typography component="legend">
								Enter Search Query
							</Typography>
							<TextareaAutosize
								id="column"
								value={searchQuery.column}
								onChange={(e) =>
									setSearchQuery({ ...searchQuery, column: e.target.value })
								}
								style={{ width: '100%', height: 100 }}
							/>
						</Box>

						<Stack direction="row" justifyContent="flex-end" spacing={1} mt={2}>
							<Button onClick={handlePopupClear} sx={{ color: 'rgba(0, 208, 141, 1) !important' }}>
								Clear
							</Button>
							<Button
								onClick={handleApplyClick}
								disabled={!searchQuery.column.trim()}
								sx={{ color: 'rgba(0, 208, 141, 1) !important' }}
							>
								Apply
							</Button>
						</Stack>
					</Box>
				)}

				{/* ===== Table ===== */}
				{!loading ? (
					<CustomizedDataGrid
						title={<HighlightedText text="Resources" />}
						tableColumns={getColumns()}
						tableData={resourceGroupList}
						tableType="PCOBS_ResourceGroup"
						defaultColumnNames={defaultColumnNames}
						onSorting={handleSorting}
						currSorting={sortingInfo}
						filterDataOptions={filterData}
						onFilterCheck={handleFilter}
						onHeaderSelectAll={handleHeaderSelectAll}
						onFilterClick={handleAndGetFilterOptions}
						currFilter={filterInfo}
						onCellEdit={handleEditCell}
						searchDataValues={searchFilterDetails}
						onFilterSearch={handleFilterSearch}
						page={page}
						pageSize={pageSize}
						paginationData={paginationData}
						listLength={resourceGroupList?.length}
						handlepageSizeChange={handlepageSizeChange}
						handlePageChange={handlePageChange}
						isDateRangeFilter={false}
						isDisabled={false}
						isFetching={loading}
						isLighterTheme = {true}
					/>
				) : (
					<Loader />
				)}
			</Grid>

			{/* ===== Dialog (unchanged) ===== */}
			<Dialog
				open={editOpen}
				onClose={handleEditClose}
				fullWidth
				maxWidth="md"
			>
				<BulkUpdate
					bulkData={bulkSelectedRows}
					handleEditClose={handleEditClose}
				/>
			</Dialog>

		</Box>
	);
};

export default ResourceGroupListing;

